import React from 'react';
import { Bars2Icon, PlusCircleIcon } from '@heroicons/react/24/solid';
import { OneLineInput } from '../⚛️atoms/OneLineInput';
import { TiptapEditor } from '../⚛️atoms/TiptapEditor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleArrowRight } from '@fortawesome/free-solid-svg-icons';
import ReactDragListView from 'react-drag-listview';
import { Checkbox } from '../⚛️atoms/Checkbox';
import { TrashIcon } from '@heroicons/react/24/outline';
import { MarkdownWrapper } from './MarkdownWrapper';
import useLockedTranslate from '../../Hooks/useLockedTranslate';
import { getTranslation, updateTranslation } from '../../utils/translationHelper';
import { TranslateButton } from '../atoms/translateButton';
import { cloneWorkbookV2Value } from './editorUtils';

export const WorkbookV2List = ({ item, updateItem, editing, editingLanguage, workbook }) => {
  const { lang } = useLockedTranslate(workbook);

  const updateEditorItem = (updates) => {
    const nextItem = cloneWorkbookV2Value(item);
    nextItem.text = updates.text ?? nextItem.text;
    nextItem.page_number = updates.page_number ?? nextItem.page_number;
    nextItem.translations = updates.translations ?? nextItem.translations;
    nextItem.options = {
      ...(nextItem.options ?? {}),
      ...(updates.options ?? {})
    };
    updateItem(nextItem);
  };

  const listItems = item.options?.items ?? [];
  const optionTranslations = item.options?.translations ?? {};
  const isOrdered = item.options?.ordered;

  const getListItems = () => {
    return (
      <>
        {listItems.map((listItem, index) => (
          <li key={`${listItem}-${index}`} className="my-2 flex items-start gap-3">
            {!isOrdered && <FontAwesomeIcon icon={faCircleArrowRight} className="text-pdteal pt-1" />}
            {isOrdered && (
              <span className="text-pdteal mr-2 font-bold">{index > 9 ? index + 1 : `0${index + 1}` }.</span>
            )}
            <div className="flex items-center gap-2">
              <MarkdownWrapper className="flex items-center gap-2">
                {lang === 'en' ? listItem : getTranslation(optionTranslations, lang, listItem, listItem)}
              </MarkdownWrapper>
            </div>
          </li>
        ))}
      </>
    );
  };

  const getList = () => {
    if (isOrdered) {
      return <ol className="mb-4 ml-8">{getListItems()}</ol>;
    }

    return <ul className="mb-4 ml-8">{getListItems()}</ul>;
  };

  const updateListItem = (value, index) => {
    const nextItems = listItems.map((entry, entryIndex) => (entryIndex === index ? value : entry));
    updateEditorItem({ options: { items: nextItems } });
  };

  const dragProps = {
    onDragEnd(fromIndex, toIndex) {
      const nextItems = [...listItems];
      const movedItem = nextItems.splice(fromIndex, 1)[0];
      nextItems.splice(toIndex, 0, movedItem);
      updateEditorItem({ options: { items: nextItems } });
    },
    nodeSelector: 'div',
    handleSelector: 'svg'
  };

  return (
    <div className={`flex w-full items-center gap-2 ${editing && 'border-pdteal rounded-lg border-2 px-8 py-12'}`}>
      {!editing && (
        <div className="flex flex-col">
          <MarkdownWrapper>{lang === 'en' ? item.text : getTranslation(item.translations, lang, 'text', item.text)}</MarkdownWrapper>
          <>{getList()}</>
        </div>
      )}
      {editing && (
        <div className="grid w-full grid-cols-2 gap-8">
          <div className="relative">
            <p className="mb-2 text-xl/6 font-medium text-gray-900">List Title</p>
            <TiptapEditor content={item.text} onChange={(value) => updateEditorItem({ text: value })} />
            {editingLanguage && (
              <TranslateButton
                text={item.text}
                language={editingLanguage}
                onResponse={(value) =>
                  updateEditorItem({
                    translations: updateTranslation(item.translations ?? {}, editingLanguage, 'text', value)
                  })
                }
              />
            )}
          </div>
          {editingLanguage && (
            <div>
              <p className="mb-2 text-xl/6 font-medium text-gray-900">List Title ({editingLanguage})</p>
              <TiptapEditor
                content={getTranslation(item.translations, editingLanguage, 'text')}
                onChange={(value) =>
                  updateEditorItem({
                    translations: updateTranslation(item.translations ?? {}, editingLanguage, 'text', value)
                  })
                }
              />
            </div>
          )}
          <div className="col-start-1 w-2/5">
            <OneLineInput
              value={item.page_number}
              label="Page Number"
              onChange={(value) => updateEditorItem({ page_number: value })}
            />
          </div>
          <div className="self-start">
            <Checkbox
              checked={isOrdered}
              onChange={(value) => updateEditorItem({ options: { ordered: value } })}
              label="Ordered?"
            />
          </div>
          <div className="col-span-2 flex w-full flex-col pl-3">
            <span className="pb-4 text-2xl">Items:</span>
            <ReactDragListView {...dragProps}>
              {listItems.map((listItem, index) => (
                <div className="flex items-center gap-8 pb-8" key={`${listItem}-${index}`}>
                  <Bars2Icon className="size-10"></Bars2Icon>
                  <div className="relative w-full">
                    <OneLineInput
                      value={listItem}
                      onChange={(value) => updateListItem(value, index)}
                      label={`Item ${index + 1}`}
                    />
                    {editingLanguage && (
                      <TranslateButton
                        text={listItem}
                        language={editingLanguage}
                        onResponse={(value) =>
                          updateEditorItem({
                            options: {
                              translations: updateTranslation(optionTranslations, editingLanguage, listItem, value)
                            }
                          })
                        }
                      />
                    )}
                  </div>
                  {editingLanguage && (
                    <OneLineInput
                      value={getTranslation(optionTranslations, editingLanguage, listItem)}
                      onChange={(value) =>
                        updateEditorItem({
                          options: {
                            translations: updateTranslation(optionTranslations, editingLanguage, listItem, value)
                          }
                        })
                      }
                      label={`Item ${index + 1} (${editingLanguage})`}
                    />
                  )}
                  <TrashIcon
                    className="text-pdred hover size-8 cursor-pointer"
                    onClick={() => updateEditorItem({ options: { items: listItems.toSpliced(index, 1) } })}
                  />
                </div>
              ))}
            </ReactDragListView>
            <PlusCircleIcon
              className="text-pdteal size-12 self-center drop-shadow-md hover:cursor-pointer"
              onClick={() => updateEditorItem({ options: { items: [...listItems, ''] } })}
            />
          </div>
        </div>
      )}
    </div>
  );
};
